import React from "react";
import { cloneDeep, get } from "lodash";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import { decimalPriceRegex, removeSpecialCharAndNumbers } from "../../../utils";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import CtaComponent from "../../../components/reusable-ui/cta";
import { Modal } from "@mui/material";
import { isEditMode } from "../../../helpers/catalog-helper";

interface Props {
  data?: any;
  modeConfig?: any;
  setModeConfig?: any;
  updateChargeDataInParentState?: any;
  taxErrorMap?: any;
  taxTypeDataMap?: any;
  taxAreaDataMap?: any;
  onModalClose?: any;
  handleInputChange?: any;
  isTaxEditMode?: boolean;
  onSaveCtaClick?: any;
}

const TaxDetailsModal = (props: Props) => {
  const {
    data,
    taxErrorMap = {},
    modeConfig,
    taxTypeDataMap,
    onModalClose,
    handleInputChange,
    onSaveCtaClick,
  } = props;
  /**
   * This function will render the customTextField input with the help of textFieldConfig
   * @param textFieldConfig
   */
  function renderTextField(textFieldConfig: any): JSX.Element {
    return (
      <CustomTextField
        textBoxLabel={textFieldConfig.fieldLabel}
        placeholder={textFieldConfig.placeHolder}
        regexCheck={textFieldConfig.regexCheck || removeSpecialCharAndNumbers}
        handleChange={textFieldConfig.onSubmit}
        customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal
          ${textFieldConfig.customClass || ""}`}
        parentClass={`${textFieldConfig.boxWidth || ""} ${
          textFieldConfig.parentClass || ""
        }`}
        value={textFieldConfig.value}
        rows={textFieldConfig.rows}
        isMandatory={!!textFieldConfig.isMandatory}
        errorMessage={textFieldConfig?.errorMessage || ""}
        iconClass={textFieldConfig.iconClass || ""}
        maxLength={textFieldConfig.maxLength || 8}
      ></CustomTextField>
    );
  }

  function renderCustomDropDown(dropdownConfig: any): JSX.Element {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.textBoxLabel}
        optionsMap={dropdownConfig.optionsList}
        isMultiSelect={dropdownConfig.isMultiSelect || false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={(data: any) => {
          if (dropdownConfig.getListItemText)
            return dropdownConfig.getListItemText(data);
          else return data?.name || "";
        }}
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={dropdownConfig.onSubmit}
        preSelectedValues={dropdownConfig.preSelectedValues}
        uniqueKey={dropdownConfig.uniqueKey || "mid"}
        isShowLoadMore={dropdownConfig.isShowLoadMore}
        onLoadMoreClick={dropdownConfig.onLoadMoreClick}
        onSearchApi={dropdownConfig.onSearchInput}
        isMandatory={!!dropdownConfig.isMandatory}
        errorMessage={taxErrorMap[dropdownConfig.key] || ""}
        enableSelectAll={!!dropdownConfig.enableSelectAll}
      ></CustomDropdown>
    );
  }

  const onAddNewSecondaryTaxClick = () => {
    let derivedTaxesList = cloneDeep(data?.derivedTaxes) || [];
    handleInputChange(
      [...derivedTaxesList, { editMode: true }],
      "derivedTaxes"
    );
  };

  const handleSecondaryTaxInputChange = (
    value: any,
    index: number,
    fieldToUpdate: string
  ) => {
    let updatedDerivedTax = data?.derivedTaxes?.length
      ? cloneDeep(data?.derivedTaxes[index])
      : {};
    updatedDerivedTax[fieldToUpdate] = value;
    let derivedTaxesList = data?.derivedTaxes?.length
      ? data?.derivedTaxes?.map((derivedTax: any, ind: number) => {
          return ind === index ? updatedDerivedTax : derivedTax;
        })
      : [];

    handleInputChange(derivedTaxesList, "derivedTaxes");
  };

  const onDeleteIconClick = (index: number) => {
    let derivedTaxesList = data?.derivedTaxes?.length
      ? data?.derivedTaxes?.filter((derivedTax: any, ind: number) => {
          return ind !== index;
        })
      : [];

    handleInputChange(derivedTaxesList, "derivedTaxes");
  };

  const onEditIconClick = (index: number) => {
    let updatedDerivedTax = data?.derivedTaxes?.length
      ? cloneDeep(data?.derivedTaxes[index])
      : {};
    updatedDerivedTax["editMode"] = true;
    let derivedTaxesList = data?.derivedTaxes?.length
      ? data?.derivedTaxes?.map((derivedTax: any, ind: number) => {
          return ind === index ? updatedDerivedTax : derivedTax;
        })
      : [];

    handleInputChange(derivedTaxesList, "derivedTaxes");
  };

  const onSaveTickIconClick = (index: number) => {
    let updatedDerivedTax = data?.derivedTaxes?.length
      ? cloneDeep(data?.derivedTaxes[index])
      : {};
    if (
      updatedDerivedTax?.name &&
      (updatedDerivedTax?.percentage || updatedDerivedTax?.percentage === 0)
    ) {
      delete updatedDerivedTax.editMode;
      delete updatedDerivedTax.errMsgMap;
    } else {
      let errMsgMap: any = {};
      if (!updatedDerivedTax?.name) {
        errMsgMap.name = "This field is mandatory";
      }

      if (
        !updatedDerivedTax?.percentage &&
        updatedDerivedTax?.percentage !== 0
      ) {
        errMsgMap.percentage = "This field is mandatory";
      }
      updatedDerivedTax.errMsgMap = errMsgMap;
    }
    let derivedTaxesList = data?.derivedTaxes?.length
      ? data?.derivedTaxes?.map((derivedTax: any, ind: number) => {
          return ind === index ? updatedDerivedTax : derivedTax;
        })
      : [];

    handleInputChange(derivedTaxesList, "derivedTaxes");
  };

  return (
    <Modal open={true} onClose={onModalClose}>
      <section className="absolute w-[636px] h-[668px] rounded-[8px] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white p-[24px]">
        <div className="flex items-center justify-between mb-[8px]">
          <div className="flex items-center">
            <div
              className="rista-sprite ic-arrow-left cursor-pointer"
              onClick={onModalClose}
            ></div>
            <h1 className="text-[22px] font-semibold">
              {isEditMode(modeConfig?.type) ? "Edit Tax" : "Add Tax"}
            </h1>
          </div>
          <div className="flex items-center">
            <CtaComponent
              ctaText={"Cancel"}
              ctaType={CTA_TYPE.secondary_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onModalClose}
              customClass={" mr-10"}
            ></CtaComponent>
            <CtaComponent
              ctaText={"Save Tax"}
              ctaType={CTA_TYPE.active_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onSaveCtaClick}
              customClass={" ml-10"}
            ></CtaComponent>
          </div>
        </div>

        <div className="w-full h-[576px] overflow-y-auto py-[16px]">
          {renderCustomDropDown({
            textBoxLabel: "Primary Tax",
            optionsList: taxTypeDataMap || {},
            placeholder: "Select Primary Tax",
            getListItemText: (data: any) => data?.name || "",
            dropdownHeading: "Primary Taxes",
            searchPlaceholder: "Search Primary Tax",
            parentClass: "w-full mb-[6px]",
            isMultiSelect: false,
            isMandatory: true,
            preSelectedValues: data?.taxTypeId || [],
            onSubmit: (value: any) => handleInputChange(value[0], "taxTypeId"),
            key: "taxTypeId",
            uniqueKey: "mid",
          })}

          {renderTextField({
            placeHolder: "Enter Percentage",
            fieldLabel: "Percentage",
            value: get(data, "percentage") || "",
            boxWidth: "w-full",
            isMandatory: true,
            errorMessage: taxErrorMap["percentage"],
            key: "percentage",
            regexCheck: (val: any) => decimalPriceRegex(val, 2),
            parentKey: "",
            onSubmit: (value: any) =>
              handleInputChange(value !== "" ? value : "", "percentage"),
            parentClass: "mb-[6px]",
          })}

          <div className="border border-[#EBEBEB] rounded-[8px] mt-[16px]">
            <div className="flex items-center justify-between p-[16px] border-b border-[#EBEBEB]">
              <h1 className="text-[18px] text-[#666666] font-medium leading-none">
                Secondary Tax (Tax on Tax)
              </h1>
              {data?.derivedTaxes?.length ? (
                <p
                  className="text-[14px] text-[#0074FB] font-semibold cursor-pointer"
                  onClick={onAddNewSecondaryTaxClick}
                >
                  +Add More
                </p>
              ) : null}
            </div>

            {!data?.derivedTaxes?.length ? (
              <div
                className="text-center p-[16px] text-[16px] font-semibold text-[#0074FB] cursor-pointer"
                onClick={onAddNewSecondaryTaxClick}
              >
                + Add Secondary Tax
              </div>
            ) : null}

            {data?.derivedTaxes?.length
              ? data?.derivedTaxes.map((derivedTax: any, index: number) => {
                  return (
                    <div
                      key={`derived-tax-${index}`}
                      className="p-[16px] border-b border-[#EBEBEB] last:border-b-0 relative"
                    >
                      {derivedTax?.editMode ? (
                        <>
                          <p className="text-[12px] text-[#9A3131]">
                            {derivedTax?.errMsgMap?.msg || ""}
                          </p>
                          {renderTextField({
                            placeHolder: "Enter Name",
                            fieldLabel: "Secondary Tax Name",
                            value: derivedTax?.name || "",
                            boxWidth: "w-full",
                            isMandatory: true,
                            errorMessage: derivedTax?.errMsgMap?.name,
                            key: "name",
                            regexCheck: (val: any) => val,
                            parentKey: "",
                            onSubmit: (value: any) =>
                              handleSecondaryTaxInputChange(
                                value,
                                index,
                                "name"
                              ),
                            parentClass: "mb-[6px]",
                            maxLength: 25,
                          })}
                          {renderTextField({
                            placeHolder: "Enter Percentage",
                            fieldLabel: "Percentage",
                            value: derivedTax?.percentage || "",
                            boxWidth: "w-full",
                            isMandatory: true,
                            errorMessage: derivedTax?.errMsgMap?.percentage,
                            key: "percentage",
                            regexCheck: decimalPriceRegex,
                            parentKey: "",
                            onSubmit: (value: any) =>
                              handleSecondaryTaxInputChange(
                                value !== "" ? value : "",
                                index,
                                "percentage"
                              ),
                            parentClass: "mb-[6px]",
                          })}
                          <div className="flex items-center absolute right-[8px] top-[6px]">
                            <div
                              className="rista-sprite ic-delete-grey mr-[8px]"
                              onClick={() => onDeleteIconClick(index)}
                            ></div>
                            <div
                              className="rista-sprite ic-tick-blue bg-[#F2F8FF] rounded-[6px] scale-[0.6] cursor-pointer"
                              onClick={() => onSaveTickIconClick(index)}
                            ></div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <h1 className="text-[16px] font-semibold mr-[12px]">
                              {derivedTax?.name}
                            </h1>
                            <p className="text-[13px] font-regular">
                              {derivedTax?.percentage} %
                            </p>
                          </div>
                          <div className="flex items-center">
                            <div
                              className="rista-sprite ic-pencil-black mr-[8px]"
                              onClick={() => onEditIconClick(index)}
                            ></div>
                            <div
                              className="rista-sprite ic-delete-grey"
                              onClick={() => onDeleteIconClick(index)}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      </section>
    </Modal>
  );
};

export default TaxDetailsModal;
